import {Modal, Descriptions, Table, Divider, Tooltip, Tag, Col, Button, Row} from "antd";
import {CheckOutlined, CloseOutlined, UndoOutlined} from "@ant-design/icons";
import moment from "moment";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../hooks/useStore";
import {CircleButton} from "../../../_defaultComponents/CircleButton/CircleButton";
import {SelectField, QuickReactionContainer, Title} from "./ShiftSuggestionUpdateModalStyled";
import {StatusTag} from "../StatusTag/StatusTag";

const ShiftUpdateModal = ({
    data,
    visibility,
    onCancel,
    quickReactionData,
    forecastId,
    setRefresh
}) => {
    const {t} = useTranslation(["shiftSuggestion"]);
    const stores = useStore();
    const [actionConfirmLoading, setActionConfirmLoading] = useState(false);
    const [courierId, setCourierId] = useState("");
    const [quickReactionCourier, setQuickReactionCourier] = useState("");
    const [quickReactionButtonDisabled, setQuickReactionButtonDisabled] = useState(true);

    const title = `${data?.warehouse?.companyName} - ${data?.warehouse?.name}`;
    const renderActionButtons = (id, item) => {
        if (item.status != "waitingApprove") {
            return (
                <>
                    <StatusTag status={item.status} />
                    {["approved", "rejected"].includes(item.status) &&
                        item.presentationStatus === "waitingApprove" && (
                            <Tooltip title={t("revert", {ns: "shiftSuggestion"})}>
                                <CircleButton
                                    data-testid="revertButton"
                                    disabled={actionConfirmLoading}
                                    loading={actionConfirmLoading === id}
                                    onClick={() => onRevert(id, item)}
                                    icon={<UndoOutlined />}
                                    size="small"
                                    shape="circle"
                                ></CircleButton>
                            </Tooltip>
                        )}
                </>
            );
        }
        return (
            <>
                <Tooltip title={t("approve", {ns: "shiftSuggestion"})}>
                    <CircleButton
                        data-testid="deleteButton"
                        disabled={actionConfirmLoading}
                        loading={actionConfirmLoading === id}
                        onClick={() => onApprove(id, item)}
                        icon={<CheckOutlined />}
                    ></CircleButton>
                </Tooltip>

                <Tooltip title={t("reject", {ns: "shiftSuggestion"})}>
                    <CircleButton
                        data-testid="updateButton"
                        disabled={actionConfirmLoading}
                        loading={actionConfirmLoading === id}
                        onClick={() => onReject(id, item)}
                        icon={<CloseOutlined />}
                    ></CircleButton>
                </Tooltip>
            </>
        );
    };

    const onApprove = async (id, item) => {
        setActionConfirmLoading(true);
        item.status = "processing";
        const result = await stores.shiftSuggestionStore.approvePreference({
            preferenceId: id,
            forecastId
        });
        if (!result) {
            item.status = "waitingApprove";
            setActionConfirmLoading(false);
        } else {
            setTimeout(() => {
                item.status = "approved";
                setActionConfirmLoading(false);
            }, 1000);
        }
    };

    const onReject = async (id, item) => {
        setActionConfirmLoading(true);
        item.status = "processing";
        const result = await stores.shiftSuggestionStore.rejectPreference({
            preferenceId: id,
            forecastId
        });
        if (!result) {
            item.status = "waitingApprove";
            setActionConfirmLoading(false);
        } else {
            setTimeout(() => {
                item.status = "rejected";
                setActionConfirmLoading(false);
            }, 1000);
        }
    };

    const onRevert = async (id, item) => {
        const oldStatus = item.status;

        setActionConfirmLoading(true);
        item.status = "processing";
        const result = await stores.shiftSuggestionStore.revertPreference({
            preferenceId: id
        });
        if (!result) {
            item.status = oldStatus;
            setActionConfirmLoading(false);
        } else {
            setTimeout(() => {
                item.status = "waitingApprove";
                setActionConfirmLoading(false);
            }, 1000);
        }
    };

    const handleChange = value => {
        setQuickReactionButtonDisabled(!value);
        setCourierId(value);
        setQuickReactionCourier(value);
    };
    const handleClick = async () => {
        stores.appStore.openConfirmModal({
            title: t("confirmTitle", {ns: "shiftSuggestion"}),
            content: t("quickReactionConfirmMessage", {
                ns: "shiftSuggestion"
            }),
            onOk: async () => {
                await stores.shiftStore.postQuickReactionCourierList({
                    courierId,
                    forecastId
                });
                setRefresh(prev => prev + 1);
                setQuickReactionCourier("");
                setQuickReactionButtonDisabled(true);
            }
        });
    };

    return (
        <Modal
            width={700}
            title={title}
            visible={visibility}
            footer={null}
            onCancel={() => {
                onCancel(false);
            }}
        >
            <Descriptions size="small" bordered>
                <Descriptions.Item span={3} label={t("date", {ns: "shiftSuggestion"})}>
                    {moment(data?.date).format("DD.MM.YYYY")}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("hour", {ns: "shiftSuggestion"})}>
                    {moment(data?.startTime).format("HH:mm")} -{" "}
                    {moment(data?.endTime).format("HH:mm")} (
                    {moment.duration(moment(data?.endTime).diff(data?.startTime)).asHours()}{" "}
                    {t("units.hour.long", {ns: "common"})})
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("location", {ns: "shiftSuggestion"})}>
                    {data?.warehouse?.city?.capitalize()} / {data?.warehouse?.county} /{" "}
                    {data?.warehouse?.district}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("upperRegion", {ns: "shiftSuggestion"})}>
                    {data?.warehouse?.upperRegionGroup?.capitalize()}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("courierType", {ns: "shiftSuggestion"})}>
                    {t(data?.courierType, {ns: "courierTypes"})}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("plannedCount", {ns: "shiftSuggestion"})}>
                    {data?.plannedCourierCount}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("approved", {ns: "shiftSuggestion"})}>
                    {data?.preferences.filter(p => p.status == "approved").length}
                </Descriptions.Item>
                <Descriptions.Item span={3} label={t("rejected", {ns: "shiftSuggestion"})}>
                    {data?.preferences.filter(p => p.status == "rejected").length}
                </Descriptions.Item>
            </Descriptions>
            <Divider />

            <Table
                style={{overflowX: "auto"}}
                rowKey="_id"
                dataSource={data?.preferences.filter(p => p.status != "deleted")}
                pagination={false}
                data-testid="shiftPreferencesTable"
            >
                <Table.Column
                    dataIndex="courier"
                    title={t("courierName", {ns: "shiftSuggestion"})}
                    render={courier => <span data-testid="courierName">{courier.fullname}</span>}
                />
                <Table.Column
                    dataIndex="preferenceOrder"
                    title={t("preferenceOrder", {ns: "shiftSuggestion"})}
                    render={text => <span data-testid="preferenceOrder">{text}</span>}
                />
                <Table.Column
                    dataIndex="_id"
                    title={t("action", {ns: "shiftSuggestion"})}
                    render={renderActionButtons}
                />
            </Table>

            <QuickReactionContainer>
                <Title>{t("screens.quickReaction", {ns: "common"})}</Title>
                <Row type="flex" justify="space-between" gutter={[0, 20]}>
                    <Col span={18}>
                        <SelectField
                            value={quickReactionCourier}
                            onChange={value => handleChange(value)}
                            optionFilterProp="children"
                            data-testid="courierStatusSelect"
                            filterOption={(input, option) =>
                                option.children.props.children.props.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children.props.children.props.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(
                                        optionB.children.props.children.props.children
                                            .toString()
                                            .toLowerCase()
                                    )
                            }
                        >
                            {quickReactionData?.map(data => (
                                <SelectField.Option
                                    key={data._id}
                                    value={data._id}
                                    data-testid="courierStatusSelect_option"
                                >
                                    <span>{`${data.fullname}`}</span>
                                </SelectField.Option>
                            ))}
                        </SelectField>
                    </Col>
                    <Col>
                        <Button disabled={quickReactionButtonDisabled} onClick={handleClick}>
                            <span>{t("approve", {ns: "shiftSuggestion"})}</span>
                        </Button>
                    </Col>
                </Row>
            </QuickReactionContainer>
        </Modal>
    );
};

export default ShiftUpdateModal;
